import React, { useEffect, useState } from 'react';
import JobsiteCard from '../JobsiteCard';
import MapService, { PinVisibility } from './MapService';
import { toast } from 'react-toastify';

interface JobsiteMapViewProps {
  isOnline: boolean;
  isSidebarLoading: boolean;
  jobSites: any[];
  selectedJobsiteId: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  getCompletionPercentage: (status: string) => number;
  handleJobsiteNameClick: (site: any) => void;
  handleJobsiteCardClick: (site: any) => void;
  handlePageChange: (page: number) => void;
  allJobsitesForMap: any[];
  mechanicalContractors: any[];
  setSelectedJobsiteId: (id: string) => void;
  focusJobsiteId: string | null;
}

const JobsiteMapView: React.FC<JobsiteMapViewProps> = ({
  isOnline,
  isSidebarLoading,
  jobSites,
  selectedJobsiteId,
  currentPage,
  pageSize,
  totalItems,
  getCompletionPercentage,
  handleJobsiteNameClick,
  handleJobsiteCardClick,
  handlePageChange,
  allJobsitesForMap,
  mechanicalContractors,
  setSelectedJobsiteId,
  focusJobsiteId,
}) => {
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [pinVisibility, setPinVisibility] = useState<PinVisibility>({
    headOffice: false,
    subOffice: true,
    union: true,
    nonUnion: true,
    mixed: true,
    noVisits: true,
  });

  // Initialize map once when component mounts
  useEffect(() => {
    const initializeMap = async () => {
      if (!(MapService as any).map) {
        try {
          setIsMapLoading(true);

          const mapOptions = {
            jobSites: allJobsitesForMap,
            activeFilters: { contractor: [] },
            onMarkerClick: (siteId: string) => {
              setSelectedJobsiteId(siteId);
            },
            focusJobsiteId: focusJobsiteId || null,
            mechanicalContractors: mechanicalContractors
          };

          const success = await MapService.initializeMap('map', mapOptions);
          if (!success) {
            console.error('Failed to initialize map');
            toast.error('Failed to initialize map. Please try again.');
          }
        
        } catch (error) {
          console.error('Error initializing map:', error);
          toast.error('Failed to load map. Please try again.');
        } finally {
          setIsMapLoading(false);
        }
      }
    };

    initializeMap();
  }, []);

  // Rebuild pin data when jobsite/contractor data changes
  useEffect(() => {
    if ((MapService as any).map) {
      MapService.updateMarkers({
        jobSites: allJobsitesForMap,
        activeFilters: { contractor: [] },
        onMarkerClick: (siteId: string) => {
          setSelectedJobsiteId(siteId);
        },
        focusJobsiteId: focusJobsiteId || null,
        mechanicalContractors: mechanicalContractors,
        visiblePinTypes: pinVisibility,
      });
    }
  }, [allJobsitesForMap]);

  // Fast visibility toggle — no data rebuild, just flips layer visibility
  useEffect(() => {
    if ((MapService as any).map) {
      MapService.updateLayerVisibility(pinVisibility);
    }
  }, [pinVisibility]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      MapService.cleanup();
    };
  }, []);

  return (
    <div className="flex flex-col lg:flex-row">
      {isOnline ? (
        <div className="flex-1 overflow-hidden relative min-h-[400px] lg:min-h-[66vh]">
          {isMapLoading && (
            <div className="absolute inset-0 bg-white flex items-center justify-center z-10 rounded-[12px_0px_0px_12px]">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-t-4 border-[#A52A2A] border-t-transparent animate-spin mb-4"></div>
                <p className="text-[#A52A2A] font-medium">Loading map...</p>
                <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
              </div>
            </div>
          )}

          <div>
            <div
              id="map"
              className="w-full h-[400px] lg:h-[66vh] rounded-[12px_0px_0px_0px]"
              data-testid="google-map"
            ></div>
            <div className="bottom-4 left-4 bg-white p-2 rounded-bl-lg shadow-sm">
              <div className="flex justify-center md:flex-row md:gap-4 flex-col">
                <div className="mb-2 md:mb-0">
                  <div className="font-semibold text-xs text-gray-600 mb-1">Jobsites</div>
                  <div className="flex flex-col md:flex-row md:gap-3 gap-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.union}
                        onChange={() => setPinVisibility(prev => ({ ...prev, union: !prev.union }))}
                        className="w-3 h-3 accent-[#22C55E]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#22C55E]"></div>
                      <span className="text-xs">Union</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.nonUnion}
                        onChange={() => setPinVisibility(prev => ({ ...prev, nonUnion: !prev.nonUnion }))}
                        className="w-3 h-3 accent-[#A42823]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#A42823]"></div>
                      <span className="text-xs">Non-Union</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.mixed}
                        onChange={() => setPinVisibility(prev => ({ ...prev, mixed: !prev.mixed }))}
                        className="w-3 h-3 accent-[#F97316]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#F97316]"></div>
                      <span className="text-xs">Mixed</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.noVisits}
                        onChange={() => setPinVisibility(prev => ({ ...prev, noVisits: !prev.noVisits }))}
                        className="w-3 h-3 accent-[#6B7280]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#6B7280]"></div>
                      <span className="text-xs">No Visits</span>
                    </label>
                  </div>
                </div>
                <div className="border-l border-gray-300 hidden md:block"></div>
                <div>
                  <div className="font-semibold text-xs text-gray-600 mb-1">Mechanical Contractors</div>
                  <div className="flex flex-col md:flex-row md:gap-3 gap-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.headOffice}
                        onChange={() => setPinVisibility(prev => ({ ...prev, headOffice: !prev.headOffice }))}
                        className="w-3 h-3 accent-[#1E88E5]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#1E88E5]"></div>
                      <span className="text-xs">Head Office</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pinVisibility.subOffice}
                        onChange={() => setPinVisibility(prev => ({ ...prev, subOffice: !prev.subOffice }))}
                        className="w-3 h-3 accent-[#952ee9]"
                      />
                      <div className="w-3 h-3 rounded-full bg-[#952ee9ff]"></div>
                      <span className="text-xs">Sub Office</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isMapLoading && jobSites.length === 0 && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-[12px_0px_0px_12px]">
              <div className="flex flex-col items-center text-center p-4">
                <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Jobsites Found</h3>
                <p className="text-sm text-gray-500">No jobsites match your search criteria or have valid coordinates</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-hidden relative min-h-[400px] lg:min-h-[66vh] lg:min-v-[50vh]">
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-[12px_0px_0px_12px]">
            <div className="flex flex-col items-center text-center p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-1">You are in offline mode</h3>
              <p className="text-sm text-gray-500">Map view not available in offline mode.</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full lg:w-[380px]">
        <div className="h-[400px] lg:h-[66vh] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto scrollbar-hide relative">
            {isSidebarLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                <div className="w-8 h-8 rounded-full border-4 border-t-4 border-[#A52A2A] border-t-transparent animate-spin"></div>
              </div>
            )}
            <div className="space-y-4 m-2.5">
              {jobSites.length > 0 ? (
                jobSites.map((site) => (
                  <div
                    id={`jobsite-card-${site.id}`}
                    key={site.id}
                    className={`transition-all duration-300 ${
                      selectedJobsiteId === site.id ? 'ring-1 rounded-md ring-[#A52A2A] shadow-lg transform scale-[1.02]' : ''
                    }`}
                  >
                    <JobsiteCard
                      site={site}
                      onClick={() => handleJobsiteNameClick(site)}
                      onNameClick={() => handleJobsiteCardClick(site)}
                      getCompletionPercentage={getCompletionPercentage}
                    />
                  </div>
                ))
              ) : (
                <div className="h-[400px] lg:h-[66vh] flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-gray-500 p-4 text-center">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-lg sm:text-xl font-medium mb-1">No Jobsites Found!</p>
                    <p className="text-xs sm:text-sm text-gray-400">No jobsites match your search criteria</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Sticky Pagination - Only show when online */}
          {jobSites.length > 0 && isOnline && (
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-border pt-2 px-2 z-10">
              <div className="flex items-center justify-between">
                <div className="text-small text-text-secondary">
                  Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`btn-secondary p-2 rounded-full ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:border-text-secondary'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-small text-text-primary">{currentPage}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage * pageSize >= totalItems}
                    className={`btn-secondary p-2 rounded-full ${
                      currentPage * pageSize >= totalItems ? 'opacity-50 cursor-not-allowed' : 'hover:border-text-secondary'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsiteMapView; 